/**
 * CitySystem.js
 *
 * Phase 9 — Cities & Settlements for the KRAKEN 3D gameplay loop.
 *
 * Owns:
 *   - Deterministic procedural city rendering driven entirely by the backend
 *     /api/city payload (so all players see identical layouts).
 *   - Walkable building meshes (towers, blocks, domes, warehouses) + lit
 *     plaza and a clearly marked landing pad in the center.
 *   - Player-proximity detection: emits `nearCity` + distance for the HUD
 *     so React can show the "Press E · Enter <city>" prompt and toggle the
 *     city overlay modal.
 *   - Mini-radar POI export (single city ping) so the orientation widget
 *     can guide the player toward the plaza from anywhere on the body.
 *
 * Composed into GameScene — call city.tick(dt) every frame, and feed it a
 * city payload via `city.setCity(data)` once the React layer fetches
 * `/api/city`. Pure Babylon.js + plain JS.
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

// --- Tunables ---

// "Close enough" distance to surface the "Press E to enter city" prompt and
// allow opening the overlay. Tied to the city plaza radius from the backend.
const PROMPT_RADIUS_PADDING = 4.5;

// Y-offset baked into building positions so they sit a hair above the
// ground plane to avoid z-fighting (the GameScene terrain is sampled here
// per-building via the supplied getTerrainHeight callback).
const Y_SETTLE = 0.05;

// Color helpers
function hexToColor3(hex) {
  const h = (hex || '#cfd6e2').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return new Color3(r, g, b);
}

export class CitySystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this.onCityProximity = opts.onCityProximity || (() => {});

    this._root = new TransformNode('cityRoot', scene);
    this._meshes = [];
    this._city = null;
    this._lastPromptState = false;
    this._lastDistance = Infinity;
    // Cached world position of the plaza center (after build)
    this._plazaWorld = new Vector3(0, 0, 0);
    // Phase 12c — interior kiosks
    this._kiosks = [];           // [{ id, type, pos, tab, mesh }]
    this._nearKioskState = null; // { type, distance, tab } or null
  }

  /**
   * Receive the backend city payload and build (or rebuild) the 3D scene.
   * Idempotent — calling with the same coords/planet won't rebuild.
   */
  setCity(data) {
    if (!data) {
      this.clear();
      return;
    }
    if (this._city
      && this._city.coords_key === data.coords_key
      && this._city.planet_idx === data.planet_idx) {
      return; // identical city already built
    }
    this.clear();
    this._city = data;
    this._build(data);
  }

  clear() {
    for (const m of this._meshes) {
      try { m.material?.dispose?.(); } catch (_e) { /* noop */ }
      try { m.dispose(); } catch (_e) { /* noop */ }
    }
    this._meshes = [];
    this._city = null;
    this._lastPromptState = false;
    this._lastDistance = Infinity;
    this._kiosks = [];
    this._nearKioskState = null;
  }

  /** Build buildings, plaza disc, landing pad and ambient torches. */
  _build(data) {
    const plazaOffset = data.plaza_offset || { x: 0, z: 0 };
    const baseY = this.getTerrainHeight(plazaOffset.x, plazaOffset.z) || 0;
    this._plazaWorld = new Vector3(plazaOffset.x, baseY, plazaOffset.z);

    // Plaza disc (subtle paving)
    const plaza = MeshBuilder.CreateDisc('cityPlaza', {
      radius: (data.plaza_radius || 12) + 2,
      tessellation: 48,
      sideOrientation: 2, // double-sided
    }, this.scene);
    plaza.rotation.x = Math.PI / 2;
    plaza.position.set(plazaOffset.x, baseY + Y_SETTLE + 0.02, plazaOffset.z);
    const plazaMat = new StandardMaterial('cityPlazaMat', this.scene);
    plazaMat.diffuseColor = new Color3(0.18, 0.20, 0.24);
    plazaMat.emissiveColor = new Color3(0.05, 0.06, 0.08);
    plazaMat.specularColor = new Color3(0, 0, 0);
    plaza.material = plazaMat;
    this._meshes.push(plaza);

    // Buildings
    for (const b of (data.buildings || [])) {
      this._addBuilding(b, plazaOffset.x, plazaOffset.z, baseY);
    }

    // Phase 12c — Hub interior with kiosks (shop/missions/refuel)
    this._buildHubInterior(plazaOffset.x, plazaOffset.z, data.plaza_radius || 12, baseY);

    // Four corner beacons (helps players sight the city from far away)
    const beaconRadius = (data.plaza_radius || 12) + 16;
    for (let i = 0; i < 4; i++) {
      const ang = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const bx = plazaOffset.x + Math.cos(ang) * beaconRadius;
      const bz = plazaOffset.z + Math.sin(ang) * beaconRadius;
      const by = this.getTerrainHeight(bx, bz) || 0;
      const post = MeshBuilder.CreateCylinder(
        `cityBeacon_${i}`,
        { diameter: 0.6, height: 12 },
        this.scene,
      );
      post.position.set(bx, by + 6, bz);
      const pm = new StandardMaterial(`cityBeaconMat_${i}`, this.scene);
      pm.diffuseColor = new Color3(0.1, 0.12, 0.18);
      pm.emissiveColor = new Color3(0.95, 0.78, 0.30);
      pm.specularColor = new Color3(0, 0, 0);
      post.material = pm;
      this._meshes.push(post);
    }
  }

  _addBuilding(b, ox, oz, baseY) {
    const wx = ox + Number(b.x || 0);
    const wz = oz + Number(b.z || 0);
    const gy = this.getTerrainHeight(wx, wz) || baseY;
    const color = hexToColor3(b.color || '#cfd6e2');

    if (b.kind === 'pad') {
      // Landing pad — a low cylinder with concentric stripes
      const pad = MeshBuilder.CreateCylinder('cityPad', {
        diameter: Math.max(b.width || 12, 6),
        height: 0.4,
        tessellation: 48,
      }, this.scene);
      pad.position.set(wx, gy + 0.21, wz);
      const padMat = new StandardMaterial('cityPadMat', this.scene);
      padMat.diffuseColor = new Color3(0.10, 0.11, 0.14);
      padMat.emissiveColor = new Color3(0.05, 0.07, 0.10);
      padMat.specularColor = new Color3(0, 0, 0);
      pad.material = padMat;
      this._meshes.push(pad);

      // Glowing ring on the pad edge
      const ring = MeshBuilder.CreateTorus('cityPadRing', {
        diameter: Math.max(b.width || 12, 6) - 0.2,
        thickness: 0.18,
        tessellation: 64,
      }, this.scene);
      ring.position.set(wx, gy + 0.45, wz);
      const ringMat = new StandardMaterial('cityPadRingMat', this.scene);
      ringMat.diffuseColor = new Color3(0.30, 0.65, 0.95);
      ringMat.emissiveColor = new Color3(0.20, 0.60, 1.0);
      ringMat.specularColor = new Color3(0, 0, 0);
      ring.material = ringMat;
      this._meshes.push(ring);
      return;
    }

    let mesh;
    if (b.kind === 'dome') {
      mesh = MeshBuilder.CreateSphere(`bld_dome_${this._meshes.length}`, {
        diameter: Math.max(b.width, 1.5),
        segments: 16,
        slice: 0.5,
      }, this.scene);
      mesh.position.set(wx, gy + Y_SETTLE, wz);
    } else if (b.kind === 'tower') {
      mesh = MeshBuilder.CreateBox(`bld_tower_${this._meshes.length}`, {
        width: b.width,
        depth: b.depth,
        height: b.height,
      }, this.scene);
      mesh.position.set(wx, gy + b.height / 2 + Y_SETTLE, wz);
    } else if (b.kind === 'warehouse') {
      mesh = MeshBuilder.CreateBox(`bld_wh_${this._meshes.length}`, {
        width: b.width,
        depth: b.depth,
        height: b.height,
      }, this.scene);
      mesh.position.set(wx, gy + b.height / 2 + Y_SETTLE, wz);
    } else {
      // "block" / fallback
      mesh = MeshBuilder.CreateBox(`bld_blk_${this._meshes.length}`, {
        width: b.width,
        depth: b.depth,
        height: b.height,
      }, this.scene);
      mesh.position.set(wx, gy + b.height / 2 + Y_SETTLE, wz);
    }
    mesh.rotation.y = Number(b.rotation || 0);
    const mat = new StandardMaterial(`bldMat_${this._meshes.length}`, this.scene);
    mat.diffuseColor = color;
    mat.emissiveColor = new Color3(0.06, 0.07, 0.09);
    mat.specularColor = new Color3(0.02, 0.02, 0.02);
    mesh.material = mat;
    this._meshes.push(mesh);

    // Windowed glow strip on towers/blocks/warehouses — adds night legibility
    if (b.kind !== 'dome') {
      const stripHeight = Math.min(0.45, b.height * 0.10);
      const strip = MeshBuilder.CreateBox(`bldStrip_${this._meshes.length}`, {
        width: b.width * 0.92,
        depth: b.depth * 0.92,
        height: stripHeight,
      }, this.scene);
      strip.position.set(wx, gy + b.height * 0.55 + Y_SETTLE, wz);
      strip.rotation.y = Number(b.rotation || 0);
      const stripMat = new StandardMaterial(`bldStripMat_${this._meshes.length}`, this.scene);
      stripMat.diffuseColor = new Color3(0.15, 0.18, 0.22);
      stripMat.emissiveColor = new Color3(0.95, 0.80, 0.45);
      stripMat.specularColor = new Color3(0, 0, 0);
      stripMat.alpha = 0.92;
      strip.material = stripMat;
      this._meshes.push(strip);
    }
  }

  /** Distance from player to plaza center on the XZ plane. */
  _distanceToPlaza() {
    const p = this.getPlayerPos();
    const dx = p.x - this._plazaWorld.x;
    const dz = p.z - this._plazaWorld.z;
    return Math.hypot(dx, dz);
  }

  // ---------- Phase 12c — Hub interior ----------

  /** Build 3 small kiosk buildings around the plaza (Phase 13 P3 — replaces
   *  the single open-air hub from 12c). Each building has 4 walls with an
   *  open doorway facing the plaza, a glowing rim cap, and a single
   *  interactive kiosk inside that the player can approach + press E. */
  _buildHubInterior(ox, oz, plazaRadius, baseY) {
    const wallMat = new StandardMaterial('kioskWallMat', this.scene);
    wallMat.diffuseColor = new Color3(0.45, 0.50, 0.58);
    wallMat.emissiveColor = new Color3(0.06, 0.08, 0.12);
    wallMat.specularColor = new Color3(0.05, 0.05, 0.05);

    const floorMat = new StandardMaterial('kioskFloorMat', this.scene);
    floorMat.diffuseColor = new Color3(0.20, 0.24, 0.30);
    floorMat.emissiveColor = new Color3(0.10, 0.14, 0.22);
    floorMat.specularColor = new Color3(0, 0, 0);

    const buildings = [
      { id: 'kiosk_shop', type: 'shop', tab: 'shop', label: 'Shop',
        angle: 0.0, color: new Color3(0.95, 0.85, 0.35) },
      { id: 'kiosk_missions', type: 'missions', tab: 'contracts', label: 'Missions',
        angle: 2 * Math.PI / 3, color: new Color3(0.65, 0.40, 1.0) },
      { id: 'kiosk_refuel', type: 'refuel', tab: 'refit', label: 'Refuel',
        angle: -2 * Math.PI / 3, color: new Color3(0.30, 0.85, 1.0) },
    ];

    this._kiosks = [];
    const ringR = plazaRadius + 12;
    const inner = 4.5;   // half-width of the building (inner room is ~9m)
    const wallH = 4.0;

    for (const b of buildings) {
      const cx = ox + Math.cos(b.angle) * ringR;
      const cz = oz + Math.sin(b.angle) * ringR;
      const gy = this.getTerrainHeight(cx, cz) || baseY;
      // Vector from building center toward plaza — doorway faces this side.
      const toPlazaX = -Math.cos(b.angle);
      const toPlazaZ = -Math.sin(b.angle);
      const yaw = Math.atan2(toPlazaX, toPlazaZ);

      // Floor
      const floor = MeshBuilder.CreateBox(b.id + '_floor', {
        width: inner * 2.2, depth: inner * 2.2, height: 0.18,
      }, this.scene);
      floor.position.set(cx, gy + 0.09, cz);
      floor.rotation.y = yaw;
      floor.material = floorMat;
      this._meshes.push(floor);

      // Build 4 walls in local coords (door at -Z faces the plaza); we'll
      // rotate the building so -Z points at the plaza by setting wall yaw.
      const halfDoor = 1.4;       // door is 2.8m wide
      // Helper: place a box in local space and rotate around the building center.
      const placeLocal = (name, lx, lz, lw, ld, lh, ly) => {
        const m = MeshBuilder.CreateBox(name, { width: lw, depth: ld, height: lh }, this.scene);
        // Apply local-to-world rotation around (cx, cz)
        const cosY = Math.cos(yaw);
        const sinY = Math.sin(yaw);
        const wx = cx + lx * cosY + lz * sinY;
        const wz = cz - lx * sinY + lz * cosY;
        m.position.set(wx, gy + ly, wz);
        m.rotation.y = yaw;
        m.material = wallMat;
        this._meshes.push(m);
        return m;
      };

      // Back wall (+Z relative to door direction; door direction is -Z, so back is +Z)
      placeLocal(b.id + '_back', 0, inner, inner * 2 + 0.4, 0.5, wallH, wallH / 2);
      // Left wall (-X)
      placeLocal(b.id + '_left', -inner, 0, 0.5, inner * 2 + 0.4, wallH, wallH / 2);
      // Right wall (+X)
      placeLocal(b.id + '_right', inner, 0, 0.5, inner * 2 + 0.4, wallH, wallH / 2);
      // Front wall has a doorway — split into two segments on either side of the door
      const sideLen = (inner * 2 - halfDoor * 2) / 2;
      const offset = halfDoor + sideLen / 2;
      placeLocal(b.id + '_front_l', -offset, -inner, sideLen, 0.5, wallH, wallH / 2);
      placeLocal(b.id + '_front_r', offset, -inner, sideLen, 0.5, wallH, wallH / 2);
      // Door header (lintel above the doorway)
      placeLocal(b.id + '_lintel', 0, -inner, halfDoor * 2, 0.5, 0.6, wallH - 0.3);
      // Glowing rim cap on top — emissive tinted to the kiosk color
      const rim = MeshBuilder.CreateBox(b.id + '_rim', {
        width: inner * 2.4, depth: inner * 2.4, height: 0.25,
      }, this.scene);
      rim.position.set(cx, gy + wallH + 0.05, cz);
      rim.rotation.y = yaw;
      const rimMat = new StandardMaterial(b.id + '_rimMat', this.scene);
      rimMat.diffuseColor = b.color.scale(0.25);
      rimMat.emissiveColor = b.color.scale(0.7);
      rimMat.specularColor = new Color3(0, 0, 0);
      rim.material = rimMat;
      this._meshes.push(rim);

      // Doorway accent (glowing strip on the lintel)
      const arch = MeshBuilder.CreateBox(b.id + '_arch_glow', {
        width: halfDoor * 2 + 0.2, depth: 0.1, height: 0.1,
      }, this.scene);
      const cosY = Math.cos(yaw);
      const sinY = Math.sin(yaw);
      arch.position.set(
        cx + 0 * cosY + (-inner + 0.08) * sinY,
        gy + wallH - 0.7,
        cz - 0 * sinY + (-inner + 0.08) * cosY,
      );
      arch.rotation.y = yaw;
      const archMat = new StandardMaterial(b.id + '_arch_mat', this.scene);
      archMat.diffuseColor = new Color3(0.05, 0.05, 0.08);
      archMat.emissiveColor = b.color;
      archMat.specularColor = new Color3(0, 0, 0);
      arch.material = archMat;
      this._meshes.push(arch);

      // Pedestal kiosk inside, near the back wall
      const kx = cx + 0 * cosY + (inner * 0.55) * sinY;
      const kz = cz - 0 * sinY + (inner * 0.55) * cosY;
      const ky = gy;
      const pedestal = MeshBuilder.CreateCylinder(b.id + '_ped', {
        diameter: 1.4, height: 0.9, tessellation: 16,
      }, this.scene);
      pedestal.position.set(kx, ky + 0.45, kz);
      const pedMat = new StandardMaterial(b.id + '_pedMat', this.scene);
      pedMat.diffuseColor = new Color3(0.18, 0.20, 0.25);
      pedMat.emissiveColor = new Color3(0.05, 0.06, 0.10);
      pedMat.specularColor = new Color3(0, 0, 0);
      pedestal.material = pedMat;
      this._meshes.push(pedestal);

      const panel = MeshBuilder.CreateBox(b.id + '_panel', {
        width: 1.1, height: 1.4, depth: 0.08,
      }, this.scene);
      panel.position.set(kx, ky + 1.7, kz);
      panel.rotation.y = yaw;
      const panelMat = new StandardMaterial(b.id + '_panelMat', this.scene);
      panelMat.diffuseColor = b.color.scale(0.2);
      panelMat.emissiveColor = b.color;
      panelMat.specularColor = new Color3(0, 0, 0);
      panelMat.alpha = 0.85;
      panel.material = panelMat;
      this._meshes.push(panel);

      this._kiosks.push({
        id: b.id,
        type: b.type,
        tab: b.tab,
        label: b.label,
        pos: new Vector3(kx, ky, kz),
      });
    }
  }

  /** Returns the kiosk currently within interact range (or null). */
  _nearestKiosk() {
    if (!this._kiosks || this._kiosks.length === 0) return null;
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = 3.0; // interact threshold
    for (const k of this._kiosks) {
      const d = Math.hypot(p.x - k.pos.x, p.z - k.pos.z);
      if (d < bestDist) {
        bestDist = d;
        best = { ...k, distance: d };
      }
    }
    return best;
  }

  /** Bearing from player to plaza in degrees (0..360, 0 = +Z forward). */
  _bearingToPlaza(forwardYaw) {
    const p = this.getPlayerPos();
    const dx = this._plazaWorld.x - p.x;
    const dz = this._plazaWorld.z - p.z;
    const ang = Math.atan2(dx, dz); // world bearing
    let rel = ang - (forwardYaw || 0);
    while (rel > Math.PI) rel -= Math.PI * 2;
    while (rel < -Math.PI) rel += Math.PI * 2;
    return rel; // radians, signed (-PI..PI)
  }

  /** Frame tick — recomputes proximity + emits HUD state when it changes. */
  tick(_dt) {
    if (!this._city) return;
    const dist = this._distanceToPlaza();
    this._lastDistance = dist;
    const triggerRadius = (this._city.plaza_radius || 12) + PROMPT_RADIUS_PADDING;
    const nearCity = dist <= triggerRadius;

    // Phase 12c — kiosk proximity check
    const kiosk = this._nearestKiosk();
    const prevKiosk = this._nearKioskState;
    const kioskChanged = !!kiosk !== !!prevKiosk
      || (kiosk && prevKiosk && (kiosk.type !== prevKiosk.type
          || Math.abs(kiosk.distance - prevKiosk.distance) > 0.15));
    if (kioskChanged) {
      this._nearKioskState = kiosk ? { type: kiosk.type, tab: kiosk.tab, label: kiosk.label, distance: kiosk.distance } : null;
    }

    if (nearCity !== this._lastPromptState || kioskChanged) {
      this._lastPromptState = nearCity;
      try {
        this.onCityProximity({
          nearCity,
          cityName: this._city.name || 'Colony',
          distance: dist,
          nearKiosk: this._nearKioskState,
        });
      } catch (_e) { /* noop */ }
    }
  }

  /** Read-only state */
  getState() {
    if (!this._city) {
      return { hasCity: false, nearCity: false, distance: 0, name: null, nearKiosk: null };
    }
    return {
      hasCity: true,
      nearCity: this._lastPromptState,
      distance: this._lastDistance,
      name: this._city.name || 'Colony',
      plaza: { x: this._plazaWorld.x, z: this._plazaWorld.z },
      coords_key: this._city.coords_key,
      planet_idx: this._city.planet_idx,
      nearKiosk: this._nearKioskState,
    };
  }

  /** Phase 12b — plaza world position (used by NpcSystem to seed pedestrians). */
  getPlazaWorld() {
    return this._plazaWorld.clone();
  }

  /** Phase 12b — plaza radius from the backend payload (12 default). */
  getPlazaRadius() {
    return this._city?.plaza_radius || 12;
  }

  /** Single radar POI (distance + bearing from player heading). */
  getRadarPoi(forwardYaw) {
    if (!this._city) return null;
    const dist = this._lastDistance;
    const bearing = this._bearingToPlaza(forwardYaw);
    return {
      type: 'city',
      label: this._city.name || 'Colony',
      distance: dist,
      bearing,
      color: '#facc15', // amber pin — distinct from mining + enemy pings
    };
  }

  dispose() {
    this.clear();
    try { this._root.dispose(); } catch (_e) { /* noop */ }
  }
}

export default CitySystem;
